<script>
import speakerDetail from './speaker-detail.vue'
import channelPrivateButton from '../channel/channel-private-button.vue'
import Toast from '../lib/toast.vue'

export default {
  components: { speakerDetail, channelPrivateButton, Toast },
  props: {
    contactId: {type: String, required: true},
  },

  data() {
    return {
      contact: {},
      observed: this.$storage.observed,
      toastMessage: '',
    }
  },

  computed: {
    isSpeaker() { return !!this.contact.speaker },
    hasNetworkingInfo() {
      const c = this.contact
      return !!(c.is_booth_staff || c.is_visitor || c.speaker || c.role || c.challenges
                || (c.interests && c.interests.length))
    },
    directChannel() {
      // Touch the observed list so Vue re-evaluates on storage updates.
      this.observed.directChannels
      return this.$storage.getDirectChannel(this.contactId)
    },
    // "Kontakt löschen" only makes sense once a connection was accepted
    // (legacy channels report 'accepted' too).
    hasAcceptedChannel() {
      return !!(this.directChannel && this.directChannel.connection_state === 'accepted')
    },
  },

  created() {
    this.fetchContact()
  },
  methods: {
    contactDisplay(contact) {
      return `${contact.first_name} ${contact.last_name}`.trim() || contact.username
    },

    fetchContact() {
      let me = this
      this.$api.get(`/community-users/${this.contactId}/`, ).then(function(response){
        me.contact = response.data
      })
    },

    removeChannelFromStorage(channel) {
      this.$storage.observed.directChannels = this.$storage.observed.directChannels.filter(c => c.id !== channel.id)
      this.$storage.observed.contactIds = this.$storage.observed.contactIds.filter(id => id !== this.contactId)
      this.$storage.setItem('direct-channels', this.$storage.observed.directChannels)
      this.$storage.setItem('contact-ids', this.$storage.observed.contactIds)
    },

    routeBackSkippingChannel(channelId) {
      // The view we came from may be the just-deleted direct channel
      // (channel header links to this detail view). Skip that history
      // entry - back() would re-enter a dead channel and fail.
      let last = this.$router.lastRoute
      if (channelId && last && last.params && last.params.channelId === channelId) {
        this.$router.go(-2)
      } else {
        this.$router.back()
      }
    },

    deleteContact() {
      let channel = this.$storage.getDirectChannel(this.contactId)
      if (!channel) return
      if (!confirm('Kontakt löschen? Alle Nachrichten werden gelöscht.')) return
      this.$chat.delete(`channels/${channel.id}/`).then(() => {
        this.removeChannelFromStorage(channel)
        this.routeBackSkippingChannel(channel.id)
      })
    },

    showActionError(error) {
      let data = error && error.response && error.response.data
      this.toastMessage = (data && data.error) || 'Aktion fehlgeschlagen.'
    },

    blockContact() {
      if (!confirm('Kontakt blockieren? Ein bestehender Verlauf wird gelöscht und neue Anfragen werden verhindert.')) return
      this.$chat.post('connections/block/', {user_uuid: this.contactId}).then(() => {
        let channel = this.$storage.getDirectChannel(this.contactId)
        if (channel) this.removeChannelFromStorage(channel)
        // Stay on the page: the button switches to "Blockiert" (server state).
        this.fetchContact()
      }).catch(error => this.showActionError(error))
    },

    unblockContact() {
      if (!confirm('Blockierung aufheben?')) return
      this.$chat.delete('connections/block/', {data: {user_uuid: this.contactId}})
        .then(() => this.fetchContact())
        .catch(error => this.showActionError(error))
    }
  }
}

</script>

<template>
  <div>
    <span v-if="contact.speaker">
      <speaker-detail v-if="contact.speaker" :speakerId="contact.speaker.id.toString()" :descriptionOnly="true" />
    </span>
    <span v-else>
      <div class="card mb-2 p-2">
        <img :src="contact.profile_image" class="avatar mx-auto" />
        <div class="card-body text-center">
          <h4 class="card-title mb-0">{{contactDisplay(contact)}}</h4>
          <div class="text-muted"><small>({{contact.username}})</small></div>
          <p v-if="contact.job_title" class="text-muted">
            <small>{{contact.job_title}}</small>
          </p>
          <p>{{contact.company_name}}</p>
          <div class="mb-3">
            <channel-private-button v-if="contact.uuid && !contact.is_blocked_by_me" :contactId="contact.uuid" ></channel-private-button>
          </div>
        </div>
      </div>
    </span>

    <div v-if="hasNetworkingInfo" class="card mb-2 p-2">
      <div class="card-body">
        <div class="d-flex flex-wrap gap-1 mb-3">
          <span v-if="isSpeaker" class="badge bg-primary">Speaker</span>
          <span v-if="contact.is_booth_staff" class="badge bg-info text-dark">Aussteller</span>
          <span v-if="contact.is_visitor" class="badge bg-secondary">Besucher</span>
        </div>

        <div v-if="contact.role" class="mb-3">
          <div class="text-muted small text-uppercase">Rolle</div>
          <div>{{ contact.role }}</div>
        </div>

        <div v-if="contact.interests && contact.interests.length" class="mb-3">
          <div class="text-muted small text-uppercase">Interessen</div>
          <div class="d-flex flex-wrap gap-1 mt-1">
            <span v-for="cat in contact.interests" :key="cat.id" class="badge bg-light text-dark border badge-wrap">{{ cat.name }}</span>
          </div>
        </div>

        <div v-if="contact.challenges" class="mb-1">
          <div class="text-muted small text-uppercase">Aktuelle Herausforderungen</div>
          <div>{{ contact.challenges }}</div>
        </div>
      </div>
    </div>

    <div class="text-center mt-3">
      <button v-if="hasAcceptedChannel" @click="deleteContact" class="btn btn-outline-danger btn-sm me-1">
        <i class="fas fa-trash"></i> Kontakt löschen
      </button>
      <button v-if="contact.is_blocked_by_me" @click="unblockContact" class="btn btn-outline-secondary btn-sm">
        <i class="fas fa-ban"></i> Blockiert
      </button>
      <button v-else @click="blockContact" class="btn btn-danger btn-sm">
        <i class="fas fa-ban"></i> Blockieren
      </button>
    </div>

    <toast v-if="toastMessage" :message="toastMessage" type="error" @closed="toastMessage = ''" />
  </div>
</template>

<style scoped>

a { text-decoration:none}

.avatar {
  height:100px;
  width:100px;
  object-fit:cover;
  border-radius:50%;
  overflow:hidden;
}

</style>